/**
 * The closed line of every core section on the record lane (SA-461, sweep
 * spec §7.4): one sentence per card, worded here so a test reads the same
 * string under any runner locale (dates through `stepLiveText`, `now`
 * injectable) and the components stay markup. A card with no data still says
 * so — never an empty header. A module card words its own line (D9).
 */
import { recordQuote } from './quoteText'
import { summaryClause } from './customerUpdateText'
import { dayText, dueLine, lowerWords, outcomeWords, whenText } from '@/components/process/stepLiveText'
import { QUEUE_STATE_CONFIG } from '@/constants/queueStates'
import { DECISION_LABELS } from '@/components/orders/feasibilityText'
import type {
  ExternalReference, IntakeRead, Order, OrderEventItem, OrderFeasibilityState, QuoteVersion, RecordSource, RecordStatus, ReferenceSystem,
} from '@/types/commerce'
import type { RecordProcessRead } from '@/types/process'
import { formatCurrency } from '@/utils/currency'
import { describeEvent } from './eventLabels'
import { INTAKE_SLOT } from './intakeSnapshot'
import { isPlace, placeWords } from './place'

export const SUMMARY_LOADING = 'Loading…'

const join = (parts: Array<string | null | undefined | false>): string => parts.filter(Boolean).join(' · ')
const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`
const filled = (v: unknown): boolean => v !== null && v !== undefined && v !== ''

/** SA-1418 (R2): the first open task's due time is in the deployment zone, named once, as the strip's. */
export function tasksSummary(record: RecordProcessRead | null | undefined, noun: string, now: Date = new Date(), zone?: string): string {
  if (record === undefined) return SUMMARY_LOADING
  if (record === null) return 'none open; no process runs'
  const [first] = record.open_tasks
  if (!first) return record.status === 'running' ? 'none open; the step runs on its own' : `none open; the ${noun.toLowerCase()} is closed`
  const line = join([`${first.step_number} ${first.step_title}`, first.seat_label, first.due_at && `due ${dueLine(first.due_at, now, zone)}`])
  return `${record.open_tasks.length} open · ${line}`
}

export function quotesSummary(versions: QuoteVersion[] | undefined): string {
  if (versions === undefined) return SUMMARY_LOADING
  const latest = recordQuote(versions)
  if (!latest) return 'none recorded yet'
  return `v${latest.versionNumber} · ${formatCurrency(latest.amountCents, latest.currency)} · ${latest.expired ? 'expired' : latest.status}`
}

export const STATUS_SOURCE_WORDS: Record<RecordStatus['source'], string> = {
  step: 'from the current step',
  outcome: 'from the outcome',
  condition: 'from the quote',
  override: 'set by hand',
}

export function statusSummary(status: RecordStatus | null | undefined): string {
  return status ? `${status.label} · ${STATUS_SOURCE_WORDS[status.source]}` : 'no status yet'
}

export function queueSummary(order: Pick<Order, 'queueState' | 'assignedTo'>, assigneeNames: Record<string, string> = {}): string {
  const state = order.queueState ? (QUEUE_STATE_CONFIG[order.queueState]?.label ?? lowerWords(order.queueState)) : 'no queue state yet'
  const name = order.assignedTo ? assigneeNames[order.assignedTo] : null
  const owner = !order.assignedTo ? 'unassigned' : name ? `assigned to ${name}` : 'assigned'
  return `${state} · ${owner}`
}

export function activitySummary(events: OrderEventItem[] | undefined, noun: string, now: Date = new Date()): string {
  if (events === undefined) return SUMMARY_LOADING
  if (events.length === 0) return 'no events yet'
  const last = events.reduce((a, b) => (Date.parse(b.occurredAt) > Date.parse(a.occurredAt) ? b : a))
  const label = describeEvent(last.eventType, last.payload, noun, now).label
  return `${plural(events.length, 'event')} · last: ${label}, ${whenText(last.occurredAt, now)}`
}

export function outcomeSummary(order: Pick<Order, 'outcome' | 'closedAt'>, now: Date = new Date()): string {
  return order.outcome ? join([outcomeWords(order.outcome), dayText(order.closedAt, now)]) : 'open'
}

export function customerSummary(order: Pick<Order, 'customerName' | 'customerEmail' | 'customerPhone'>): string {
  return join([order.customerName || 'Unknown', order.customerEmail || order.customerPhone])
}

export function referencesSummary(refs: ExternalReference[] | undefined, systems: ReferenceSystem[]): string {
  if (refs === undefined) return SUMMARY_LOADING
  const [first] = refs
  if (!first) return 'none yet'
  const label = systems.find((s) => s.id === first.system)?.label ?? first.system
  return join([`${label} ${first.reference}`, refs.length > 1 && `${refs.length - 1} more`])
}

export function sourceSummary(source: RecordSource | undefined, now: Date = new Date()): string {
  if (source === undefined) return SUMMARY_LOADING
  if (source.kind === 'email') return join([source.email?.fromAddr || 'Sender not recorded', whenText(source.email?.receivedAt, now)])
  if (source.kind === 'whatsapp-group') {
    const group = source.group
    return join([group?.fromName || 'Sender not recorded', group?.subject && `in ${group.subject}`, whenText(group?.receivedAt, now)])
  }
  return source.kind === 'conversation' ? 'a conversation in the inbox' : 'none'
}

/** One text, not a list (ruling R8): its first line; the header truncates the rest. */
export function notesSummary(notes: string | null | undefined): string {
  const line = (notes ?? '').split('\n').map((l) => l.trim()).find(Boolean)
  return line ?? 'no notes'
}

export function recordSummary(order: Pick<Order, 'createdAt' | 'quotedAt' | 'paidAt' | 'deliveredAt'>, now: Date = new Date()): string {
  return join([
    order.createdAt && `created ${dayText(order.createdAt, now)}`,
    order.quotedAt && `quoted ${dayText(order.quotedAt, now)}`,
    order.paidAt && `paid ${dayText(order.paidAt, now)}`,
    order.deliveredAt && `delivered ${dayText(order.deliveredAt, now)}`,
  ]) || 'no timestamps'
}

export function intakeSummary(read: IntakeRead | null, loadError: string | null, waiting = 0): string {
  if (loadError) return 'unavailable'
  if (!read) return SUMMARY_LOADING
  const value = (id: string): string | null => {
    const v = read.fields.find((f) => f.id === id)?.value
    if (isPlace(v)) return placeWords(v) || null
    if (typeof v === 'string') return v.trim() || null
    return typeof v === 'number' ? String(v) : null
  }
  const origin = value(INTAKE_SLOT.origin)
  const destination = value(INTAKE_SLOT.destination)
  // SA-466 R1: the view marks only `extracted` rows, so the closed line is
  // where the tally is said. Omitted at zero — the line reads as it always
  // did. It goes LAST on purpose: this line is `truncate`d inside the
  // ~330px enquiry column, so a clause ahead of the pair pushes the route
  // and the size — the two facts SA-461 §7.4 put here — out of view.
  const extracted = read.fields.filter((f) => f.provenance === 'extracted').length
  return join([
    // SA-1029 (spec §D23): the one clause that must survive the truncation, so it goes FIRST.
    waiting > 0 && summaryClause(waiting),
    `${read.fields.filter((f) => filled(f.value)).length}/${read.fields.length} fields`,
    origin && destination && `${origin} → ${destination}`,
    value(INTAKE_SLOT.cargoSize),
    extracted > 0 && `${extracted} extracted`,
  ])
}

export function feasibilitySummary(state: OrderFeasibilityState | null, loading: boolean): string {
  if (loading) return SUMMARY_LOADING
  const f = state?.feasibility
  if (!f) return 'not recorded yet'
  const n = state?.scenarios?.length ?? 0
  return join([DECISION_LABELS[f.decisionState], f.limitingFactor && `limiting factor ${f.limitingFactor}`, n > 0 && plural(n, 'option')])
}

export function itemsSummary(order: Pick<Order, 'items' | 'totalCents' | 'currency'>): string {
  return `${plural(order.items?.length ?? 0, 'item')} · ${formatCurrency(order.totalCents, order.currency)}`
}

export function deliverySummary(order: Pick<Order, 'deliveryMethod' | 'deliveryDate'>, now: Date = new Date()): string {
  return join([order.deliveryMethod, dayText(order.deliveryDate, now)]) || 'not set'
}

export function detailsSummary(order: Pick<Order, 'occasion' | 'guestCount'>): string {
  return join([order.occasion, order.guestCount ? `${order.guestCount} guests` : null]) || 'see below'
}

export function paymentsSummary(order: Pick<Order, 'payments' | 'depositCents' | 'depositPaid'>): string {
  const completed = (order.payments ?? []).filter((p) => p.status === 'completed').length
  return join([
    completed > 0 ? `${plural(completed, 'payment')} completed` : 'none recorded',
    order.depositCents > 0 && `deposit ${order.depositPaid ? 'paid' : 'unpaid'}`,
  ])
}
